import { createPortal } from "react-dom";
import {
    Fragment,
    useState,
    useCallback,
    useLayoutEffect,
    useEffect,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { WalletSignTransactionError } from "@solana/wallet-adapter-base";

import { useSolanaTransfer } from "../Contexts/useSolanaTransfer";
import { useSolanaTransferModal } from "../Contexts/useSolanaTransferModal";

import NftNotAvailableError from "../Errors/NftNotAvailableError";
import InsufficientFundError from "../Errors/InsufficientFundError";
import Error4xx from "../Errors/Error4xx";
import Error5xx from "../Errors/Error5xx";
import AuthError from "../Errors/AuthError";

import { Spinner } from "./Spinner";
import { classNames } from "../Utils/helpers";

const FADE_DURATION = 200;
const TRANSFER_ERROR_MSG =
    "Oops! It looks like there was an issue when processing your request. \n Please try again later.";
const TRANSFER_SERIOUS_ERROR_MSG =
    "Oops! It looks like there was an issue when processing your request. \n Please contact us.";
const TRANSFER_USER_REJECT_ERROR_MSG =
    "Please approve the transaction on your Solana wallet.";
const TRANSFER_NFT_NOT_AVAILABLE_MSG_TPL =
    "Sorry, {0} is not available to transfer anymore.";
const TRANSFER_NO_ENOUGH_SOL_MSG_TPL =
    "Sorry, you don't have enough SOL to pay for the transaction fee. \n You need at least {0} SOL to complete the swap. Please make sure you have enough SOL before trying again. \n Your wallet has a balance of {1} SOL.";

function Button({ handleClick, isProcessing }) {
    return (
        <button
            disabled={isProcessing}
            onClick={handleClick}
            type="button"
            className="inline-flex w-full items-center justify-center rounded-full border border-transparent bg-orange-600 px-5 py-2  shadow-inner hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
        >
            {isProcessing && <Spinner color="text-gray-50" size="h-5 w-5" />}
            <span className="font-burbank text-lg font-semibold text-white lg:text-xl">
                Confirm to Swap
            </span>
        </button>
    );
}

export function SolanaTransferModal({
    selectedNFTs = [],
    onTransferSuccess = () => {},
}) {
    const { setVisible } = useSolanaTransferModal();
    const { transfer } = useSolanaTransfer();
    const [isProcessing, setIsProcessing] = useState(false);
    const [show, setShow] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            return (event.returnValue = "Just wait...");
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    const hideModal = useCallback(() => {
        setIsProcessing(false);
        setShow(false);
        setTimeout(() => {
            setVisible(false);
        }, FADE_DURATION);
    }, [setVisible]);

    const handleClose = useCallback(() => {
        if (isProcessing) {
            return;
        }
        hideModal();
    }, [hideModal, isProcessing]);

    const handleClick = useCallback(
        async (e) => {
            e.preventDefault();
            try {
                setError(null);
                setIsProcessing(true);
                let result = await transfer(selectedNFTs);
                if (result) {
                    onTransferSuccess();
                } else {
                    setIsProcessing(false);
                }
            } catch (error) {
                console.error(error);
                if (
                    error instanceof AuthError ||
                    error instanceof Error4xx ||
                    error instanceof Error5xx
                ) {
                    setError(new Error(TRANSFER_SERIOUS_ERROR_MSG));
                } else if (error instanceof InsufficientFundError) {
                    setError(
                        new Error(
                            TRANSFER_NO_ENOUGH_SOL_MSG_TPL.replace(
                                "{0}",
                                error.expectedSol
                            ).replace("{1}", error.balanceSol)
                        )
                    );
                } else if (error instanceof NftNotAvailableError) {
                    setError(
                        new Error(
                            TRANSFER_NFT_NOT_AVAILABLE_MSG_TPL.replace(
                                "{0}",
                                selectedNFTs.find(
                                    (nft) =>
                                        nft.token_address === error.mintAddress
                                )?.token_name
                            )
                        )
                    );
                } else if (
                    error instanceof WalletSignTransactionError &&
                    error?.error?.code === 4001
                ) {
                    setError(new Error(TRANSFER_USER_REJECT_ERROR_MSG));
                } else {
                    setError(new Error(TRANSFER_ERROR_MSG));
                }
                setIsProcessing(false);
            }
        },
        [selectedNFTs, transfer, onTransferSuccess]
    );

    useLayoutEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                handleClose();
            }
        };

        // Get original overflow
        const { overflow } = window.getComputedStyle(document.body);
        // Hack to enable fade in animation after mount
        setTimeout(() => setShow(true), 0);
        // Prevent scrolling on mount
        document.body.style.overflow = "hidden";
        // Listen for keydown events
        window.addEventListener("keydown", handleKeyDown, false);

        return () => {
            // Re-enable scrolling when component unmounts
            document.body.style.overflow = overflow;
            window.removeEventListener("keydown", handleKeyDown, false);
        };
    }, [handleClose]);

    return createPortal(
        <Transition.Root show={show} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter={classNames("ease-out", `duration-${FADE_DURATION}`)}
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave={classNames("ease-in", `duration-${FADE_DURATION}`)}
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter={classNames(
                                "ease-out",
                                `duration-${FADE_DURATION}`
                            )}
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave={classNames(
                                "ease-in",
                                `duration-${FADE_DURATION}`
                            )}
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                                <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                    <button
                                        hidden={isProcessing}
                                        type="button"
                                        className="rounded-md bg-white text-gray-700 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                        onClick={handleClose}
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon
                                            className="h-6 w-6"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                                <div className="text-left">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-bold leading-7 text-gray-800"
                                    >
                                        Notes to swap
                                    </Dialog.Title>
                                    {error !== null && (
                                        <div className="mt-6 rounded bg-orange-50 p-4">
                                            <p className="whitespace-pre-line text-sm leading-5 text-orange-600">
                                                {error.message}
                                            </p>
                                        </div>
                                    )}
                                    <ul className="mt-6 space-y-4">
                                        <li
                                            key={0}
                                            className="flex items-start justify-start gap-x-3"
                                        >
                                            <span className="block h-6 w-6 shrink-0 rounded-full bg-purple-600 text-center text-xs font-bold leading-6 text-white">
                                                1
                                            </span>
                                            <p className="text-base font-medium text-gray-700">
                                                Your Solana Neocollectible will
                                                be{" "}
                                                <b>
                                                    sent to our official cold
                                                    wallet
                                                </b>
                                                .
                                            </p>
                                        </li>
                                        <li
                                            key={1}
                                            className="flex items-start justify-start gap-x-3"
                                        >
                                            <span className="block h-6 w-6 shrink-0 rounded-full bg-purple-600 text-center text-xs font-bold leading-6 text-white">
                                                2
                                            </span>
                                            <p className="text-base font-medium text-gray-700">
                                                Once we receive your Solana
                                                Neocollectible, a new
                                                Neocollectible will be minted
                                                and{" "}
                                                <b>
                                                    sent to your Polygon wallet
                                                </b>
                                                .
                                            </p>
                                        </li>
                                        <li
                                            key={2}
                                            className="flex items-start justify-start gap-x-3"
                                        >
                                            <span className="block h-6 w-6 shrink-0 rounded-full bg-purple-600 text-center text-xs font-bold leading-6 text-white">
                                                3
                                            </span>
                                            <p className="text-base font-medium text-gray-700">
                                                Please{" "}
                                                <b>approve the transaction</b>{" "}
                                                on your Solana wallet.
                                            </p>
                                        </li>
                                        <li
                                            key={3}
                                            className="flex items-start justify-start gap-x-3"
                                        >
                                            <span className="block h-6 w-6 shrink-0 rounded-full bg-purple-600 text-center text-xs font-bold leading-6 text-white">
                                                4
                                            </span>
                                            <p className="text-base font-medium text-gray-700">
                                                Ensure the domain shown on the
                                                Solana wallet for the
                                                transaction is our official one.
                                            </p>
                                        </li>
                                    </ul>

                                    <p className="mt-6 text-base font-medium text-gray-700">
                                        Please do not close the website until
                                        then, otherwise, you may lose your
                                        asset.
                                    </p>

                                    <div className="mt-6">
                                        <p className="text-sm font-bold text-gray-800">
                                            Our official domain
                                        </p>
                                        <div className="mt-2 overflow-hidden text-ellipsis rounded bg-gray-100 py-3 px-4 text-center">
                                            <span
                                                title="swap.neopetsmeta.io"
                                                className="text-sm font-medium text-gray-500"
                                            >
                                                swap.neopetsmeta.io
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-6">
                                        <Button
                                            handleClick={handleClick}
                                            isProcessing={isProcessing}
                                        />
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>,
        document.body
    );
}
